import { useContext, useEffect, useMemo, useState } from "react";
import StrategyCard from "@/components/NWL/StrategyCard";
import { NavLink } from "react-router-dom";
import { getCurrentStrategies } from "../../services/strategies";
import { DashboardContext, NWLContext } from "../../contexts";
import NoRecommendationIcon from "/img/nwlmap/no_rec_placeholder.png";
import { setActiveLocationId } from "../../components/utils/activeLocation";

const STRATEGY_DIFFERENCES_ONLY =
  String(import.meta.env.VITE_STRATEGY_DIFFERENCES_ONLY ?? "true").toLowerCase() === "true";

const RecommendationSection = ({ sites = [] }) => {
  return (
    <section>
      <h2 className="text-[1.8rem] text-gray-700">Network strategy recommendations</h2>

      <div className="mt-2">
        {sites.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 gap-4">
            {sites.map((site) => (
              <div key={site.id} className="col-span-1">
                <NavLink
                  to="/nwl/catchment"
                  onClick={() => {
                    setActiveLocationId(site.id);
                    localStorage.setItem(
                      "location",
                      JSON.stringify({ locationId: site.id, locationName: site.name })
                    );
                    window.dispatchEvent(new Event("nwl:locations-updated"));
                  }}
                >
                  <StrategyCard
                    site={site}
                    lastUpdated={site.lastUpdated}
                    simStatus={site.simStatus}
                    digitalTwinId={site.digitalTwinId}
                  />
                </NavLink>
              </div>
            ))}
          </div>
        ) : (
          <NoRecommendationsSection />
        )}
      </div>
    </section>
  );
};

const NoRecommendationsSection = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <img
        src={NoRecommendationIcon}
        alt="No recommendations"
        style={{ maxWidth: "300px", maxHeight: "300px" }}
      />
      <h3 className="text-3xl mt-5 mb-2">No recommendations</h3>
      <p className="text-lg">There are currently no network operational changes recommended</p>
    </div>
  );
};

const NwlDashboardView = () => {
  const { value: context } = useContext(DashboardContext);

  const {
    decisionEngine: { decisionEngineByLocation },
  } = useContext(NWLContext);

  const [sites, setSites] = useState([]);

  const [organizationId, setOrganizationId] = useState(null);
  const [locations, setLocations] = useState([]);

  const readFromStorage = () => {
    const locationsRaw = localStorage.getItem("locations");
    const locationsParsed = JSON.parse(locationsRaw || "[]");

    const security = localStorage.getItem("security");
    const orgId = JSON.parse(security || "{}")?.organizationId ?? null;

    setOrganizationId(orgId);
    setLocations(Array.isArray(locationsParsed) ? locationsParsed : []);
  };

  useEffect(() => {
    readFromStorage();

    const onLocationsUpdated = () => readFromStorage();
    window.addEventListener("nwl:locations-updated", onLocationsUpdated);

    const onStorage = (e) => {
      if (e.key === "locations" || e.key === "security") readFromStorage();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("nwl:locations-updated", onLocationsUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

useEffect(() => {
    if (!organizationId || !Array.isArray(locations) || locations.length === 0) {
      setSites([]);
      return;
    }

    const fetchStrategies = async () => {
      let results = await Promise.all(
        locations.map(async (location) => {
          const currentStrategy = await getCurrentStrategies(
            location.locationId,
            organizationId
          );

          const de = decisionEngineByLocation?.[location.locationId];

          const twinId = de?.digitalTwinId ?? null;
          const simStatus = de?.simStatus ?? null;
          const lastUpdated = de?.lastSuccessfulRefreshTime;

          const recommended = twinId ? (de?.recommendedStrategyName ?? null) : null;

          return {
            id: location.locationId,
            name: String(location.locationName ?? ""),
            digitalTwinId: twinId,
            simStatus,
            lastUpdated,
            strategy: {
              current: currentStrategy?.mode,
              recommended,
            },
          };
        })
      );

      if (STRATEGY_DIFFERENCES_ONLY) {
        results = results.filter((site) => {
          if (site.simStatus === "FAILED" || site.simStatus === "IN_PROGRESS") return true;
          if (!site.digitalTwinId) return true;
          if (!site.strategy?.recommended) return false;
          return site.strategy.current !== site.strategy.recommended;
        });
      }

      results.sort((a, b) =>
        (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" })
      );

      setSites(results);
    };

    fetchStrategies();
  }, [organizationId, locations, context.selectedLocation, decisionEngineByLocation]);

  return (
    <div className="overflow-y-auto">
      <div className="container mx-auto p-5">
        <h2 className="text-5xl font-[500]">Dashboard</h2>
        <p className="text-lg mt-2 mb-5">
          Review network strategy recommendations and track implementation decisions in the decision log.
        </p>

        <RecommendationSection sites={sites} />
      </div>
    </div>
  );
};

export default NwlDashboardView;
